import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar, CheckCircle, Clock, AlertCircle, CreditCard,
  ArrowLeft, BarChart3, Loader2, Download, TrendingUp
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

const DayDetailsPage = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDayData();
  }, [date]);

  const fetchDayData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://dailycard.co/api/dashboard/daily-transactions-summary/?start_date=${date}&end_date=${date}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      const result = await response.json();
      if (result.daily_breakdown && result.daily_breakdown.length > 0) {
        setDayData(result.daily_breakdown[0]);
      }
    } catch (error) {
      console.error("Error fetching day data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
          <p className="text-white text-lg">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (!dayData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg">لا توجد بيانات لهذا اليوم</p>
          <button
            onClick={() => navigate('/transaction/report')}
            className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            العودة للتقرير
          </button>
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const pieChartData = [...dayData.payment_methods]
    .sort((a, b) => b.total_amount - a.total_amount)
    .slice(0, 6)
    .map((method) => ({
      name: method.method_name,
      value: method.total_amount,
      count: method.count,
    }));

    // Prepare sorted payment methods for cards
  const sortedPaymentMethods = [...dayData.payment_methods].sort(
    (a, b) => b.total_amount - a.total_amount
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" dir="rtl">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 container mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/transaction/report')}
              className="p-2 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-blue-500/10 p-3 rounded-xl">
                <Calendar className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white">
                  {new Date(dayData.date).toLocaleDateString('ar-EG', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h1>
                <p className="text-gray-400 text-lg">التفاصيل الكاملة لليوم</p>
              </div>
            </div>
          </div>


        </div>

        {/* Summary Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 p-6 rounded-2xl">
            <p className="text-sm text-blue-400 mb-2">عدد المعاملات</p>
            <p className="text-4xl font-bold text-white">{dayData.deposits_count}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 p-6 rounded-2xl">
            <p className="text-sm text-green-400 mb-2">إجمالي المبلغ</p>
            <p className="text-3xl font-bold text-white">${dayData.total_amount.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20 p-6 rounded-2xl">
            <p className="text-sm text-yellow-400 mb-2">العمولة</p>
            <p className="text-3xl font-bold text-white">${dayData.total_commission.toFixed(2)}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 p-6 rounded-2xl">
            <p className="text-sm text-purple-400 mb-2">لإجمالي النهائي</p>
            <p className="text-3xl font-bold text-white">${dayData.total_combined.toLocaleString()}</p>
          </div>
        </div>

        {/* Deposit Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 p-6 rounded-2xl border border-green-500/20">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <h2 className="text-2xl font-bold text-white">معاملات مدفوعة</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-lg">عدد المعاملات</span>
                <span className="text-3xl font-bold text-white">{dayData.deposits_as_paid.count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-lg">المبلغ الإجمالي</span>
                <span className="text-3xl font-bold text-green-400">${dayData.deposits_as_paid.amount.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-lg">العمولة</span>
                <span className="text-2xl font-bold text-yellow-400">${dayData.deposits_as_paid.commission.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-green-500/20">
                <span className="text-gray-400 text-lg">متوسط المعاملة</span>
                <span className="text-xl font-semibold text-white">
                  ${(dayData.deposits_as_paid.amount / dayData.deposits_as_paid.count).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 p-6 rounded-2xl border border-orange-500/20">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-8 h-8 text-orange-400" />
              <h2 className="text-2xl font-bold text-white">معاملات كدين</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-lg">عدد المعاملات</span>
                <span className="text-3xl font-bold text-white">{dayData.deposits_as_debt.count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-lg">المبلغ الإجمالي</span>
                <span className="text-3xl font-bold text-orange-400">${dayData.deposits_as_debt.amount.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-lg">العمولة</span>
                <span className="text-2xl font-bold text-yellow-400">${dayData.deposits_as_debt.commission.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-orange-500/20">
                <span className="text-gray-400 text-lg">متوسط المعاملة</span>
                <span className="text-xl font-semibold text-white">
                  ${(dayData.deposits_as_debt.amount / dayData.deposits_as_debt.count).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Exception Amount */}
        {dayData.total_exception_amount > 0 && (
          <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 p-6 rounded-2xl border border-red-500/20 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
                <div>
                  <h2 className="text-2xl font-bold text-white">مبلغ الاستثناءات</h2>
                  <p className="text-gray-400">المبالغ الاستثنائية لهذا اليوم</p>
                </div>
              </div>
              <span className="text-4xl font-bold text-red-400">
                ${dayData.total_exception_amount.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Charts Section */}
          {/* Payment Methods Chart */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-2xl border border-gray-800">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-400" />
              طرق الدفع - الرسم البياني
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={dayData.payment_methods}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="method_name" 
                  stroke="#9ca3af"
                  angle={-45}
                  textAnchor="end"
                  height={120}
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="#9ca3af"
                  label={{ value: 'عدد المعاملات', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#9ca3af"
                  label={{ value: 'المبلغ ($)', angle: 90, position: 'insideRight', fill: '#9ca3af' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151', 
                    borderRadius: '8px' 
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value, name) => {
                    if (name === 'عدد المعاملات') return [value, name];
                    return [`$${value.toLocaleString()}`, name];
                  }}
                />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="count" 
                  fill="#8b5cf6" 
                  name="عدد المعاملات"
                  radius={[8, 8, 0, 0]}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="total_amount" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  dot={{ fill: '#10b981', r: 5 }}
                  name="المبلغ الإجمالي"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          

        {/* Payment Methods Cards */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-green-400" />
            طرق الدفع - التفاصيل
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPaymentMethods.map((method, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 p-6 hover:shadow-2xl hover:border-blue-500/30 transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-gray-400 text-sm">#{index + 1}</span>
                  </div>
                  <div
                    className="p-2 rounded-lg"
                    style={{
                      backgroundColor: `${COLORS[index % COLORS.length]}20`,
                    }}
                  >
                    <CreditCard
                      className="w-5 h-5"
                      style={{ color: COLORS[index % COLORS.length] }}
                    />
                  </div>
                </div>

                {/* Payment Method Name */}
                <h3 className="text-xl font-bold text-white mb-4 truncate">
                  {method.method_name}
                </h3>

                {/* Stats Grid */}
                <div className="space-y-3">
                  {/* Transaction Count */}
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
                    <p className="text-xs text-purple-400 mb-1">عدد المعاملات</p>
                    <p className="text-2xl font-bold text-white">{method.count}</p>
                  </div>

                  {/* Total Amount */}
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
                    <p className="text-xs text-green-400 mb-1">المبلغ الإجمالي</p>
                    <p className="text-2xl font-bold text-green-400">
                      ${method.total_amount.toLocaleString()}
                    </p>
                  </div>

                  {/* Commission */}
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">
                    <p className="text-xs text-yellow-400 mb-1">العمولة</p>
                    <p className="text-xl font-bold text-yellow-400">
                      ${method.total_commission.toFixed(2)}
                    </p>
                  </div>

                  {/* Total Combined */}
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                    <p className="text-xs text-blue-400 mb-1">لإجمالي النهائي

 </p>
                    <p className="text-xl font-bold text-blue-400">
                      ${method.total_combined.toLocaleString()}
                    </p>
                  </div>
                </div>

                
              </div>
            ))}
          </div>

          {/* Summary Card */}
          <div className="mt-6 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl border border-blue-500/30 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-blue-400" />
              إجمالي اليوم
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">المعاملات</p>
                <p className="text-2xl font-bold text-purple-400">{dayData.deposits_count}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">المبلغ</p>
                <p className="text-2xl font-bold text-green-400">
                  ${dayData.total_amount.toLocaleString()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">العمولة</p>
                <p className="text-2xl font-bold text-yellow-400">
                  ${dayData.total_commission.toFixed(2)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">الإجمالي</p>
                <p className="text-2xl font-bold text-blue-400">
                  ${dayData.total_combined.toLocaleString()}
                </p>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayDetailsPage;